import React from 'react'

import { Building2, Landmark } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import {
  CompanyCombobox,
  DepartmentCombobox,
  EntityCombobox,
} from '@/components/library-v2/ui/Combobox'
import { CreateEntityFieldShell } from '@/components/library-v2/ui/CreateEntityDialogLayout'

import type { UUID } from '@/types/Id'
import type {
  EntityAgency,
  EntityCompany,
  EntityCompanyDepartment,
} from '@/types/library/Entity'

export type PrimaryAffiliationType = 'company' | 'agency'

export const FORM_SET_VALUE_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
} as const

const AFFILIATION_TYPE_OPTIONS = [
  {
    value: 'company',
    label: 'Company',
    icon: Building2,
  },
  {
    value: 'agency',
    label: 'Agency',
    icon: Landmark,
  },
] satisfies Array<{
  value: PrimaryAffiliationType
  label: string
  icon: typeof Building2
}>

export const getDefaultAffiliationType = (initialValues?: {
  entity_agency_id?: UUID | null
  entity_company_id?: UUID | null
}): PrimaryAffiliationType => {
  return initialValues?.entity_agency_id && !initialValues.entity_company_id
    ? 'agency'
    : 'company'
}

interface PersonOrganizationFieldsProps {
  affiliationType: PrimaryAffiliationType
  companyId: UUID | null
  agencyId: UUID | null
  departmentId: UUID | null
  companies?: EntityCompany[]
  agencies?: EntityAgency[]
  departments?: EntityCompanyDepartment[]
  onCompanyChange: (companyId: UUID) => void
  onAgencyChange: (agencyId: UUID) => void
  onDepartmentChange: (departmentId: UUID) => void
  onClearCompany: () => void
  onClearAgency: () => void
  onClearDepartment: () => void
}

export const PersonOrganizationFields: React.FC<
  PersonOrganizationFieldsProps
> = ({
  affiliationType,
  companyId,
  agencyId,
  departmentId,
  companies,
  agencies,
  departments,
  onCompanyChange,
  onAgencyChange,
  onDepartmentChange,
  onClearCompany,
  onClearAgency,
  onClearDepartment,
}) => {
  return (
    <div className="[overflow-anchor:none]">
      {affiliationType === 'company' ? (
        <div className="grid grid-cols-1 gap-5 md:min-h-[63px] md:grid-cols-2">
          <CreateEntityFieldShell label="Company">
            <CompanyCombobox
              value={companyId}
              onChange={onCompanyChange}
              onClear={onClearCompany}
              companies={companies}
              placeholder="Search companies..."
            />
          </CreateEntityFieldShell>
          <CreateEntityFieldShell label="Department">
            <DepartmentCombobox
              value={departmentId}
              onChange={onDepartmentChange}
              onClear={onClearDepartment}
              departments={departments}
              disabled={!companyId}
              placeholder={
                companyId
                  ? 'Search departments...'
                  : 'Select a company first...'
              }
            />
          </CreateEntityFieldShell>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:min-h-[63px] md:grid-cols-2">
          <CreateEntityFieldShell label="Agency" fullWidth>
            <EntityCombobox
              value={agencyId}
              onChange={onAgencyChange}
              onClear={onClearAgency}
              entities={agencies}
              placeholder="Search agencies..."
            />
          </CreateEntityFieldShell>
        </div>
      )}
    </div>
  )
}

interface AffiliationTypeToggleProps {
  value: PrimaryAffiliationType
  onChange: (type: PrimaryAffiliationType) => void
}

export const AffiliationTypeToggle: React.FC<AffiliationTypeToggleProps> = ({
  value,
  onChange,
}) => {
  return (
    <div className="inline-flex h-9 w-full min-w-0 rounded-md border border-primary-200 bg-primary-50 p-0.5 dark:border-primary-800 dark:bg-primary-950 sm:w-[220px]">
      {AFFILIATION_TYPE_OPTIONS.map((option) => {
        const Icon = option.icon
        const isSelected = value === option.value

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={twMerge(
              'inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded px-2 text-sm font-medium text-primary-600 transition-colors hover:text-primary-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 dark:text-primary-400 dark:hover:text-primary-100',
              isSelected &&
                'bg-white text-primary-950 shadow-sm dark:bg-primary-900 dark:text-primary-50',
            )}
          >
            <Icon className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}
